/**
 * @fileoverview File Upload Service
 * 
 * Service for handling file upload operations with R2 storage and ElevenLabs sync
 * Provides methods for uploading files, triggering sync, and checking status
 * 
 * Requirements: 7.1, 7.2, 7.3, 7.4
 */

import { ApiClient } from '../../../utils/apiClient.js';

/**
 * File Upload Service for modular file uploads
 */
export class FileUploadService {
  constructor() {
    this.apiClient = new ApiClient(() => null); // Token getter will be provided by context
  }

  /**
   * Upload a file with R2 storage integration
   * 
   * @param {File} file - File to upload
   * @param {Object} config - Upload configuration
   * @param {function} onProgress - Progress callback
   * @returns {Promise<Object>} Upload result
   */
  async uploadFile(file, config, onProgress = null) {
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('context', config.context || 'user-kb');
      formData.append('securityLevel', config.securityLevel || 'user');
      formData.append('triggerSync', config.triggerSync ? 'true' : 'false');
      
      if (config.parrotId) {
        formData.append('parrotId', config.parrotId);
      }

      // Add metadata if provided
      if (config.metadata) {
        formData.append('metadata', JSON.stringify(config.metadata));
      }

      // Upload with progress tracking (use API client's upload for FormData)
      const response = await this.apiClient.upload(
        '/api/modules/file-upload',
        formData,
        {},
        onProgress
      );

      if (!response.success) {
        throw new Error(response.error || 'Upload failed');
      }

      return response;

    } catch (error) {
      console.error('File upload error:', error);
      throw this.handleUploadError(error);
    }
  }

  /**
   * Upload multiple files with batch processing
   * 
   * @param {File[]} files - Files to upload
   * @param {Object} config - Upload configuration
   * @param {function} onProgress - Progress callback for individual files
   * @param {function} onBatchProgress - Progress callback for overall batch
   * @returns {Promise<Object[]>} Array of upload results
   */
  async uploadFiles(files, config, onProgress = null, onBatchProgress = null) {
    const results = [];
    const errors = [];

    try {
      // Process files based on configuration
      if (config.concurrent && files.length > 1) {
        // Concurrent uploads with controlled concurrency
        const maxConcurrent = config.maxConcurrentUploads || 3;
        const batches = [];
        
        // Split files into batches
        for (let i = 0; i < files.length; i += maxConcurrent) {
          batches.push(files.slice(i, i + maxConcurrent));
        }

        // Process each batch
        for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
          const batch = batches[batchIndex];
          
          // Update batch progress
          if (onBatchProgress) {
            onBatchProgress({
              currentBatch: batchIndex + 1,
              totalBatches: batches.length,
              currentFile: batchIndex * maxConcurrent + 1,
              totalFiles: files.length
            });
          }

          // Process batch concurrently
          const batchPromises = batch.map(async (file, fileIndex) => {
            const globalIndex = files.indexOf(file);
            try {
              const result = await this.uploadFile(
                file, 
                config, 
                onProgress ? (progress) => onProgress(globalIndex, progress) : null
              );
              results.push(result);
              return result;
            } catch (error) {
              errors.push({ file, error, index: globalIndex });
              throw error;
            }
          });

          // Wait for current batch to complete
          await Promise.allSettled(batchPromises);
          
          // Add delay between batches to prevent overwhelming the server
          if (batchIndex < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      } else {
        // Sequential uploads
        for (let i = 0; i < files.length; i++) {
          try {
            // Update batch progress
            if (onBatchProgress) {
              onBatchProgress({
                currentFile: i + 1,
                totalFiles: files.length,
                fileName: files[i].name
              });
            }

            const result = await this.uploadFile(
              files[i], 
              config, 
              onProgress ? (progress) => onProgress(i, progress) : null
            );
            results.push(result);

            // Add delay between sequential uploads
            if (i < files.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 50));
            }
          } catch (error) {
            errors.push({ file: files[i], error, index: i });
          }
        }
      }

      return {
        successful: results,
        failed: errors,
        total: files.length,
        successCount: results.length,
        failureCount: errors.length
      };

    } catch (error) {
      console.error('Batch upload error:', error);
      throw error;
    }
  }

  /**
   * Trigger ElevenLabs sync for uploaded files
   * 
   * @param {Object[]} files - Array of uploaded file objects
   * @param {string} parrotId - Parrot ID for sync
   * @param {string} context - Upload context
   * @returns {Promise<Object>} Sync result
   */
  async triggerSync(files, parrotId, context = 'user-kb') {
    try {
      const response = await this.apiClient.post('/api/modules/file-upload/sync', {
        files,
        parrotId,
        context
      });

      if (!response.success) {
        throw new Error(response.error || 'Sync trigger failed');
      }

      return response.sync;

    } catch (error) {
      console.error('Sync trigger error:', error);
      throw this.handleSyncError(error);
    }
  }

  /**
   * Get upload and sync status for a file
   * 
   * @param {string} fileId - File ID
   * @returns {Promise<Object>} File status
   */
  async getFileStatus(fileId) {
    try {
      const response = await this.apiClient.get(`/api/modules/file-upload/status/${fileId}`);

      if (!response.success) {
        throw new Error(response.error || 'Failed to get file status');
      }

      return response.file;

    } catch (error) {
      console.error('Get file status error:', error);
      throw error;
    }
  }

  /**
   * Get status for multiple files
   * 
   * @param {string[]} fileIds - Array of file IDs
   * @returns {Promise<Object[]>} Array of file statuses
   */
  async getFilesStatus(fileIds) {
    try {
      const statusPromises = fileIds.map(fileId => 
        this.getFileStatus(fileId).catch(error => ({
          id: fileId,
          error: error.message
        }))
      );

      const statuses = await Promise.all(statusPromises);
      return statuses;

    } catch (error) {
      console.error('Get files status error:', error);
      throw error;
    }
  }

  /**
   * Retry failed upload
   * 
   * @param {File} file - File to retry
   * @param {Object} config - Upload configuration
   * @param {function} onProgress - Progress callback
   * @returns {Promise<Object>} Upload result
   */
  async retryUpload(file, config, onProgress = null) {
    try {
      // Add retry metadata
      const retryConfig = {
        ...config,
        metadata: {
          ...config.metadata,
          isRetry: true,
          retryAttempt: (config.metadata?.retryAttempt || 0) + 1,
          originalError: config.metadata?.lastError
        }
      };

      return await this.uploadFile(file, retryConfig, onProgress);

    } catch (error) {
      console.error('Retry upload error:', error);
      throw error;
    }
  }

  /**
   * Handle upload errors with proper error classification
   * 
   * @param {Error} error - Original error
   * @returns {Error} Classified error
   */
  handleUploadError(error) {
    // Network errors
    if (error.name === 'NetworkError' || error.code === 'NETWORK_ERROR') {
      return new Error('Network connection failed. Please check your internet connection and try again.');
    }

    // File size errors
    if (error.message?.includes('too large') || error.code === 'FILE_TOO_LARGE') {
      return new Error('File is too large. Please choose a smaller file.');
    }

    // File type errors
    if (error.message?.includes('not allowed') || error.code === 'INVALID_FILE_TYPE') {
      return new Error('File type not supported. Please choose a PDF, DOCX, TXT, or HTML file.');
    }

    // Validation errors
    if (error.code === 'VALIDATION_ERROR') {
      return new Error(`File validation failed: ${error.details?.join(', ') || error.message}`);
    }

    // Permission errors
    if (error.code === 'INSUFFICIENT_PERMISSIONS') {
      return new Error('You do not have permission to upload files in this context.');
    }

    // Server errors
    if (error.status >= 500) {
      return new Error('Server error occurred. Please try again later.');
    }

    // Default error
    return new Error(error.message || 'Upload failed. Please try again.');
  }

  /**
   * Handle sync errors with proper error classification
   * 
   * @param {Error} error - Original error
   * @returns {Error} Classified error
   */
  handleSyncError(error) {
    // ElevenLabs API errors
    if (error.message?.includes('ElevenLabs') || error.code === 'ELEVENLABS_ERROR') {
      return new Error('Failed to sync with ElevenLabs. The file was uploaded but sync failed.');
    }

    // Parrot not found errors
    if (error.code === 'PARROT_NOT_FOUND') {
      return new Error('Parrot not found or you do not have access to it.');
    }

    // Agent creation errors
    if (error.message?.includes('agent') || error.code === 'AGENT_ERROR') {
      return new Error('Failed to create or update ElevenLabs agent for sync.');
    }

    // Default sync error
    return new Error(error.message || 'Sync failed. Please try again later.');
  }

  /**
   * Validate file before upload
   * 
   * @param {File} file - File to validate
   * @param {Object} config - Upload configuration
   * @returns {Object} Validation result
   */
  validateFile(file, config) {
    const errors = [];

    // Check file existence
    if (!file) {
      errors.push('File is required');
      return { valid: false, errors };
    }

    // Check file size
    const maxSize = config.maxFileSize || (4 * 1024 * 1024); // 4MB default
    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / (1024 * 1024));
      const actualSizeMB = Math.round((file.size / (1024 * 1024)) * 100) / 100;
      errors.push(`File is too large (${actualSizeMB}MB). Maximum size is ${maxSizeMB}MB.`);
    }

    // Check file type
    const allowedTypes = config.allowedTypes || ['pdf', 'docx', 'txt', 'html'];
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !allowedTypes.includes(extension)) {
      errors.push(`File type '${extension}' is not allowed. Allowed types: ${allowedTypes.join(', ')}`);
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Format file size for display
   * 
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Generate upload progress event
   * 
   * @param {number} loaded - Bytes loaded
   * @param {number} total - Total bytes
   * @returns {Object} Progress event
   */
  createProgressEvent(loaded, total) {
    const percentCompleted = Math.round((loaded * 100) / total);
    
    return {
      loaded,
      total,
      progress: percentCompleted,
      remaining: total - loaded,
      percentCompleted
    };
  }
}

// Export singleton instance
export const fileUploadService = new FileUploadService();
